import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Text, IconButton } from "@fluentui/react";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import AddButton from "@/ui-controls/AddButton";
import SearchBox from "@/ui-controls/SearchBox";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import ShimmerList from "@/ui-controls/ShimmerList";
import NewIntegrationPanel from "@/components/integrations/catalog/NewIntegrationPanel";
import EditIntegrationPanel from "@/components/integrations/catalog/EditIntegrationPanel";
import IntegrationFilterPanel, {
  IntegrationFilterOptions,
} from "@/components/integrations/catalog/IntegrationFilterPanel";
import { Stack } from "@fluentui/react";
import {
  IntegrationDto,
  IntegrationTagDto,
  IntegrationTagMappingDto,
  listIntegrations,
  listIntegrationTags,
  listIntegrationTagMappings,
} from "@/api/integrations";

const defaultFilters: IntegrationFilterOptions = {
  statusFilter: "all",
  selectedTagIds: [],
};

const IntegrationCatalogPage = () => {
  const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
  const [tags, setTags] = useState<IntegrationTagDto[]>([]);
  const [tagMappings, setTagMappings] = useState<IntegrationTagMappingDto[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [isNewPanelOpen, setIsNewPanelOpen] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationDto | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] =
    useState<IntegrationFilterOptions>({ ...defaultFilters });
  const [appliedFilters, setAppliedFilters] =
    useState<IntegrationFilterOptions>({ ...defaultFilters });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      listIntegrations(undefined, 1000),
      listIntegrationTags(undefined, 1000),
      listIntegrationTagMappings(undefined, undefined, 1000),
    ])
      .then(([intData, tagData, mappingData]) => {
        setIntegrations(intData?.result || []);
        setTags(tagData?.result || []);
        setTagMappings(mappingData?.result || []);
      })
      .catch(() => {
        setIntegrations([]);
        setTags([]);
        setTagMappings([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build a map: integrationId -> tag info
  const integrationTagsMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string }[]>();
    for (const mapping of tagMappings) {
      const tag = tags.find((t) => t.id === mapping.integrationTagId);
      if (tag) {
        const arr = map.get(mapping.integrationId) || [];
        arr.push({ id: tag.id, name: tag.name });
        map.set(mapping.integrationId, arr);
      }
    }
    return map;
  }, [tags, tagMappings]);

  // Filtered integrations
  const filteredIntegrations = useMemo(() => {
    let items = integrations;

    // Search filter (code or name)
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      items = items.filter(
        (i) =>
          i.code.toLowerCase().includes(lower) ||
          i.name.toLowerCase().includes(lower)
      );
    }

    // Status filter
    if (appliedFilters.statusFilter === "active") {
      items = items.filter((i) => i.isActive);
    } else if (appliedFilters.statusFilter === "inactive") {
      items = items.filter((i) => !i.isActive);
    }

    // Tags filter
    if (appliedFilters.selectedTagIds.length > 0) {
      items = items.filter((i) => {
        const iTags = integrationTagsMap.get(i.id) || [];
        return appliedFilters.selectedTagIds.some((tid) =>
          iTags.some((t) => t.id === tid)
        );
      });
    }

    return items;
  }, [integrations, searchTerm, appliedFilters, integrationTagsMap]);

  const hasFiltersApplied =
    appliedFilters.statusFilter !== "all" ||
    appliedFilters.selectedTagIds.length > 0;

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setSelectedFilters({ ...defaultFilters });
    setAppliedFilters({ ...defaultFilters });
    loadData();
  };

  const handleFiltersChange = (filters: IntegrationFilterOptions) => {
    setSelectedFilters(filters);
  };

  const handleApplyFilters = (filters: IntegrationFilterOptions) => {
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    setSelectedFilters({ ...defaultFilters });
    setAppliedFilters({ ...defaultFilters });
  };

  const columns: DataGridColumn[] = [
    {
      key: "code",
      name: "Code",
      fieldName: "code",
      minWidth: 100,
      maxWidth: 200,
    },
    {
      key: "name",
      name: "Name",
      fieldName: "name",
      minWidth: 150,
      maxWidth: 300,
    },
    {
      key: "tags",
      name: "Tags",
      minWidth: 150,
      maxWidth: 300,
      onRender: (item: IntegrationDto) => {
        const itemTags = integrationTagsMap.get(item.id) || [];
        return (
          <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
            {itemTags.map((t) => (
              <Text
                key={t.id}
                styles={{
                  root: {
                    backgroundColor: "#e1dfdd",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 12,
                  },
                }}
              >
                {t.name}
              </Text>
            ))}
          </Stack>
        );
      },
    },
    {
      key: "isActive",
      name: "Status",
      fieldName: "isActive",
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: IntegrationDto) => (
        <Text
          styles={{
            root: {
              color: item.isActive ? "#107c10" : "#a80000",
              fontWeight: 600,
            },
          }}
        >
          {item.isActive ? "Active" : "Inactive"}
        </Text>
      ),
    },
    {
      key: "createdAtUtc",
      name: "Created",
      fieldName: "createdAtUtc",
      minWidth: 120,
      maxWidth: 180,
      onRender: (item: IntegrationDto) =>
        new Date(item.createdAtUtc).toLocaleDateString(),
    },
    {
      key: "actions",
      name: "Actions",
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: IntegrationDto) => (
        <MoreActionsButton
          menu={[
            {
              key: "edit",
              text: "Edit",
              iconProps: { iconName: "Edit" },
              onClick: () => {
                setSelectedIntegration(item);
                setIsEditPanelOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const handleNewSave = () => {
    setIsNewPanelOpen(false);
    loadData();
  };

  const handleEditSave = () => {
    setIsEditPanelOpen(false);
    setSelectedIntegration(null);
    loadData();
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          <div>
            <h2 className="page-breadcrumb-title">Integration Catalog</h2>
            <div style={{ color: "#605e5c", fontSize: "14px" }}>
              Manage integrations and their tag assignments
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AddButton
            text="New Integration"
            onClick={() => setIsNewPanelOpen(true)}
          />
          <SearchBox
            placeholder="Search integrations..."
            onSearch={handleSearch}
            disabled={loading}
          />
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              iconProps={{ iconName: "Filter" }}
              title="Filter integrations"
              ariaLabel="Filter integrations"
              onClick={() => setIsFilterPanelOpen(true)}
              disabled={loading}
              styles={{
                root: {
                  width: 40,
                  height: 40,
                  backgroundColor: hasFiltersApplied
                    ? "#deecf9"
                    : "transparent",
                },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color: loading ? "#a19f9d" : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
            {hasFiltersApplied && (
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  backgroundColor: "#d13438",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  zIndex: 1,
                }}
              >
                1
              </div>
            )}
          </div>
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={handleRefresh}
            disabled={loading}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: {
                fontSize: 18,
                color: loading ? "#a19f9d" : "#0078d4",
              },
              rootPressed: { backgroundColor: "#e1dfdd" },
              rootDisabled: { backgroundColor: "transparent" },
            }}
          />
        </div>
      </div>

      <IntegrationFilterPanel
        isOpen={isFilterPanelOpen}
        onDismiss={() => setIsFilterPanelOpen(false)}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFiltersChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      <div style={{ padding: "16px" }}>
        {loading ? (
          <ShimmerList numberOfColumns={6} />
        ) : (
          <DataGrid items={filteredIntegrations} columns={columns} />
        )}
      </div>

      <NewIntegrationPanel
        isOpen={isNewPanelOpen}
        onDismiss={() => setIsNewPanelOpen(false)}
        onSave={handleNewSave}
      />

      <EditIntegrationPanel
        isOpen={isEditPanelOpen}
        integration={selectedIntegration}
        tagMappings={
          selectedIntegration
            ? tagMappings.filter(
                (m) => m.integrationId === selectedIntegration.id
              )
            : []
        }
        tags={tags}
        onDismiss={() => {
          setIsEditPanelOpen(false);
          setSelectedIntegration(null);
        }}
        onSave={handleEditSave}
      />
    </>
  );
};

export default IntegrationCatalogPage;
